import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { useIntl } from 'react-intl';
import { withStyles, Link, Tooltip } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { object, func, arrayOf, bool, string, number, shape } from 'prop-types';
import { pascalCase } from 'change-case';


import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import ListContainer from '../../../list';
import { fetchOrganizationsFiltered } from '../../../../actions/user';
import { Switch } from '../../../../components';
import {
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ROLE_DEVELOPER_UUID,
  ROLE_PORTAL_ADMIN,
} from '../../../../constants';
import {
  HeaderActionButtons,
  FilterByName,
} from './controls';
import styles from './styles';
import { getIsLoading, getOrganizationListResults, getOrganizationListTotalCount } from '../../../../reducers/user';
import { accessArrayToRolesMap, getAvailableRoles, rolesMapToAccessArray } from '../utils';
import DropDownMenu from '../../../../components/DropDownMenu';
export const UserOrganizationAccess = (props) => {
  const {
    classes,
    disableEdit,
    isLoading,
    organizations,
    orgCount,
    currentUserRole,
    updateUserFormObject,
    userFormObject,
   } = props;

  const intl = getI18n(useIntl());

  const [notification, setNotification] = useState({ status: '', message: '' });
  const [filterByName, setFilterByName] = useState('');
  const [filterByAnyApplied, setFilterByAnyApplied] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsObject, setSelectedRowsObject] = useState([]);
  const [isDisableUnassignRole, setDisableUnassignRole] = useState(true);
  const [bulkRoleSelected, setBulkRoleSelected] = useState(ROLE_DEVELOPER_UUID);
  const [showOrgPubOption, setShowOrgPubOption] = useState(true);
  const [pubOrgs, setPubOrgs] = useState([]);
  const { access } = userFormObject;
  const rolesMap = accessArrayToRolesMap(access);

  const updateRolesMap = (newRolesMap) => {
    const updatedAccess = rolesMapToAccessArray(newRolesMap);
    updateUserFormObject('access', updatedAccess);
  };

  const onRoleChange = (val, uuid) => {
    updateRolesMap({
      ...rolesMap,
      [uuid]: val,
    });
  }

  const formatRoleDropDown = ({ type, uuid }, intl) =>
    (<div className={classes.roleDropdownContainer}>
      {rolesMap[uuid] && <DropDownMenu
        disabled={disableEdit}
        id="user-role-drop-down"
        data={getAvailableRoles(type, intl)}
        value={rolesMap[uuid]}
        handleChange={(val) => onRoleChange(val, uuid)}
      />}
    </div>);
 
  const formatSwitch = (uuid, onAccessChange, classes) => (
    <div className={classes.switchAccess}>
      <Switch
        disabled={disableEdit}
        id={`${uuid}-access`}
        checked={Object.keys(rolesMap).includes(uuid)}
        onChange={(e) => { onAccessChange(uuid, e) }}
      />
    </div>
  );
  
  const getLinkContent = (value, uuid) => (
    <Link underline="none" href={`/admin/console/organizations/details/${uuid}`}>
      {value}
    </Link>
  );

  const TypeHeaderTooltip = () =>
    (<div>
      <div>
        {`${intl.getI18nMessage('label.user.list.page.help.text.type.publisher')}:
          ${intl.getI18nMessage('label.user.list.page.help.text.type.publisher.desc')}`}
      </div>
      <div>
        {`${intl.getI18nMessage('label.user.list.page.help.text.type.consumer')}:
        ${intl.getI18nMessage('label.user.list.page.help.text.type.consumer.desc')}`}
      </div>
    </div>);
  
  const RuleHeaderTooltip = () =>
    (<div>
      <div>
        {`${intl.getI18nMessage('label.user.list.page.help.text.role.orgadmin')}:
          ${intl.getI18nMessage('label.user.list.page.help.text.role.orgadmin.desc')}`}
      </div>
      <div>
        {`${intl.getI18nMessage('label.user.list.page.help.text.role.orgpublisher')}:
        ${intl.getI18nMessage('label.user.list.page.help.text.role.orgpublisher.desc')}`}
      </div>
      <div>
        {`${intl.getI18nMessage('label.user.list.page.help.text.role.developer')}:
        ${intl.getI18nMessage('label.user.list.page.help.text.role.developer.desc')}`}
      </div>
    </div>);
  
  const TypeHeader = ({ classes, isTypeHeaderTooltip, label }) => (
    <div className={classes.helpIconContainer}>
      <div>
        {label}
      </div>
      <Tooltip
        title={isTypeHeaderTooltip ? <TypeHeaderTooltip /> : <RuleHeaderTooltip />}
        arrow
        placement="right"
      >
        <HelpIcon className={classes.helpIcon} />
      </Tooltip>
    </div>
  );
  
  TypeHeader.propTypes = {
    classes: object,
    isTypeHeaderTooltip: bool,
    label: object,
  }

  const getOrgListColumns = ({ classes },
    onAccessChange, intl) => [{
      id: 'name',
      label: getI18nFormattedMessage('label.organization'),
      minWidth: 400,
      format: (value, uuid) => {
        const linkEl = currentUserRole === ROLE_PORTAL_ADMIN
        ? getLinkContent(value, uuid) : value;
        return linkEl;
      },
    }, {
      id: 'type',
      label: (
        <TypeHeader
          isTypeHeaderTooltip
          classes={classes}
          label={getI18nFormattedMessage('label.organization.type')}
        />
      ),
      minWidth: 50,
      isHidden : false,
      format: (value) => pascalCase(value),
    }, {
      id: 'userOrgAccessStatus',
      secondaryId: 'uuid',
      label: getI18nFormattedMessage('label.access'),
      minWidth: 50,
      isHidden : false,
      value: (item) => formatSwitch(item.uuid, onAccessChange, classes),
    }, {
      id: 'userOrgRole',
      label: (
        <TypeHeader
          isTypeHeaderTooltip={false}
          classes={classes}
          label={getI18nFormattedMessage('label.role')}
        />
      ),
      minWidth: 100,
      isHidden : false,
      value: (item) => formatRoleDropDown(item, intl),
    },
  ];
  const getOrgs = (params) => {
    const payload = {
      page: 0,
      size: rowsPerPage,
      name: filterByName,
      ...params,
    };
    props.fetchOrganizationsFiltered(payload.size, payload.page, payload.name);
  }
  useEffect(() => {
    getOrgs();
  }, []);

  useEffect(() => {
    setDisableUnassignRole(!selectedRows.some(uuid => Object.keys(rolesMap).includes(uuid)));
    setShowOrgPubOption(selectedRows.every(uuid => pubOrgs.includes(uuid)));
  }, [selectedRows]);

  useEffect(() => {
    setPubOrgs(organizations ? organizations.filter(org => org.type === 'PUBLISHER').map(org => org.uuid) : []);
  }, [organizations]);

  const onFilterByNameChange = (value) => {
    setFilterByName(value);
    setFilterByAnyApplied(true);
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      setFilterByName(filterByName);
      getOrgs({
        page: 0,
        name: e.target.value,
      });
    }
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    getOrgs({
      page: newPage,
    });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    getOrgs({
      page: 0,
      size: newRowsPerPage,
    });
  };

  const onAssignBulkRoles = () => {
    const newRoles = {};
    selectedRows.forEach(rowUuid => {
      newRoles[rowUuid] = bulkRoleSelected;
    });
    updateRolesMap({
      ...rolesMap,
      ...newRoles,
    });
    setSelectedRows([]);
  };

  const onUnAssignBulkRoles = () => {
    const tempRolesMap = rolesMap;
    selectedRows.forEach(uuid =>{
      delete tempRolesMap[uuid];
    });
    updateRolesMap(tempRolesMap);
    setSelectedRows([]);
  };

  const onAccessChange = (uuid, isChange) => {
    if(isChange) {
      updateRolesMap({
        ...rolesMap,
        [uuid]: ROLE_DEVELOPER_UUID,
      })
    } else {
      const tempRolesMap = rolesMap;
      delete tempRolesMap[uuid];
      updateRolesMap(tempRolesMap);
    }
  }

  const noResultsMessage = filterByAnyApplied ?
  intl.getI18nMessage('label.organization.filter.no.results') :
  intl.getI18nMessage('label.organization.no.results');

  const disabledRows = [];
  if (disableEdit) {
    organizations.forEach(org => {
      disabledRows.push(org.uuid);
    });
  }

  return (
    <div id="user-org-access-container" data-apim-test="user-org-access-container">
      <ListContainer
        listPageId="user-org-access-list-page"
        isLoading={isLoading === true}
        pageBodyClass={classes.listContainer}
        pageClass={classes.list}
        notificationId="api-org-access-notifications"
        notificationStatus={notification.status}
        setNotificationStatus={(status) => setNotification({ status, message: '' })}
        notificationMessage={notification.message}
        setNotificationMessage={(message) => setNotification({ status: '', message })}
        pageHeaderTitle={''}
        addButtonLabel={intl.getI18nMessage('label.organization.list.add.button')}
        onAdd={false}
        showListHeader={false}
        pageFilterAndSortClass={classes.pageFilterAndSort}
        filterAndSortContent={(
          <FilterByName
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByName}
            placeholder={intl.getI18nMessage('label.organization.name.list.page.filter.by.name.placeholder')}
            handleChange={onFilterByNameChange}
            onKeyPress={onFilterByNameKeyPress}
          />
        )}
        bulkActionsContent={
          <HeaderActionButtons
            {...props}
            bulkActionsItemsCount={selectedRows.length}
            hasVisibleActions={true}
            hasRateQuotaActions={true}
            onAssignBulkRoles={onAssignBulkRoles}
            onUnAssignBulkRoles={onUnAssignBulkRoles}
            disableUnassignRole={isDisableUnassignRole}
            bulkRoleSelected={bulkRoleSelected}
            setBulkRoleSelected={setBulkRoleSelected}
            showOrgPub={showOrgPubOption}
          />
        }
        columns={getOrgListColumns(props, onAccessChange, intl)}
        disabledRows={disabledRows}
        rows={organizations}
        noResultsMessage={noResultsMessage}
        page={page}
        totalElements={orgCount}
        totalPages={Math.ceil(orgCount / rowsPerPage)}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selectedRowsObject={selectedRowsObject}
        setSelectedRowsObject={setSelectedRowsObject}
        hasPaginationDisabled={selectedRows && selectedRows.length > 0}
        paginationTooltipMessage={intl.getI18nMessage('label.organization.access.pagination.disabled.message')}
      />
    </div>
  );
}

const mapStateToProps = state => ({
  organizations: getOrganizationListResults(state),
  orgCount: getOrganizationListTotalCount(state),
  isLoading: getIsLoading(state),
});

const mapDispatchToProps = {
  fetchOrganizationsFiltered,
};

UserOrganizationAccess.propTypes = {
  classes: object,
  disableEdit: bool,
  organizations: arrayOf(object),
  isLoading: bool,
  orgCount: number,
  currentUserRole: string,
  fetchOrganizationsFiltered: func,
  updateUserFormObject: func,
  userFormObject: shape({}),
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(UserOrganizationAccess);
